import React, { useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../store';
import { useWaveSurfer } from '../hooks/useWaveSurfer';
import type { Finding, Severity } from '../types';

const SEVERITY_COLORS: Record<Severity, string> = {
  critical: 'rgba(239, 68, 68, 0.25)',
  major: 'rgba(249, 115, 22, 0.25)',
  minor: 'rgba(234, 179, 8, 0.25)',
};

const PlayIcon: React.FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M8 5v14l11-7z" />
  </svg>
);

const PauseIcon: React.FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
  </svg>
);

const StopIcon: React.FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
    <rect x="6" y="6" width="12" height="12" rx="1" />
  </svg>
);

function buildReferenceUrl(surah: number, ayah: number): string {
  const s = String(surah).padStart(3, '0');
  const a = String(ayah).padStart(3, '0');
  return `https://everyayah.com/data/Husary_128kbps/${s}${a}.mp3`;
}

const WaveformPlayer: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const refContainerRef = useRef<HTMLDivElement>(null);
  const audioBlobUrl = useSelector((state: RootState) => state.recitation.audioBlobUrl);
  const evaluation = useSelector((state: RootState) => state.evaluation.current);
  const ayah = useSelector((state: RootState) => state.ayah.current);

  const { wavesurfer, isPlaying, isReady, play, pause, stop } = useWaveSurfer({
    containerRef,
    audioBlobUrl,
  });

  useEffect(() => {
    if (!wavesurfer || !isReady || !evaluation) return;

    const findings: Finding[] = evaluation.findings;
    if (findings.length === 0) return;

    const duration = wavesurfer.getDuration();
    if (duration <= 0) return;

    try {
      const RegionsPlugin = (window as unknown as Record<string, unknown>).__wsRegionsLoaded;
      if (RegionsPlugin) return;

      import('wavesurfer.js/dist/plugins/regions.js').then((mod) => {
        const RegionsPluginClass = mod.default;
        const regionsPlugin = wavesurfer.registerPlugin(RegionsPluginClass.create());

        for (const finding of findings) {
          for (const span of finding.spans) {
            const startSec = span.t0Ms / 1000;
            const endSec = span.t1Ms / 1000;
            if (startSec < duration && endSec <= duration) {
              regionsPlugin.addRegion({
                start: startSec,
                end: endSec,
                color: SEVERITY_COLORS[finding.severity],
                drag: false,
                resize: false,
              });
            }
          }
        }

        regionsPlugin.on('region-clicked', (region: { start: number; end: number; play: () => void }) => {
          region.play();
        });
      });
    } catch {
      // Regions plugin not available, skip
    }
  }, [wavesurfer, isReady, evaluation]);

  // Reference waveform (comparative)
  const { wavesurfer: refWs, isPlaying: refPlaying, isReady: refReady, play: refPlay, pause: refPause } = useWaveSurfer({
    containerRef: refContainerRef,
    audioBlobUrl: evaluation && ayah ? buildReferenceUrl(ayah.surah, ayah.ayah) : null,
  });

  if (!audioBlobUrl) return null;

  return (
    <div className="card">
      <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-3 uppercase tracking-wider">
        Your Recording
      </h3>
      <div
        ref={containerRef}
        className="w-full rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800/50 mb-4"
      />
      <div className="flex items-center justify-center gap-3">
        <button
          onClick={isPlaying ? pause : play}
          disabled={!isReady}
          className="btn-secondary px-4 py-2"
        >
          {isPlaying ? <PauseIcon /> : <PlayIcon />}
        </button>
        <button
          onClick={stop}
          disabled={!isReady}
          className="btn-secondary px-4 py-2"
        >
          <StopIcon />
        </button>
      </div>
      {evaluation && evaluation.findings.length > 0 && (
        <p className="text-xs text-gray-500 text-center mt-2">
          Colored regions show detected issues. Click a region to loop it.
        </p>
      )}

      {/* Comparative: Reference waveform */}
      {evaluation && ayah && (
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700/50">
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-3 uppercase tracking-wider">
            Reference (Al-Husary)
          </h3>
          <div
            ref={refContainerRef}
            className="w-full rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800/50 mb-3"
          />
          <div className="flex items-center justify-center">
            <button
              onClick={refPlaying ? refPause : refPlay}
              disabled={!refReady}
              className="btn-secondary px-4 py-2"
            >
              {refPlaying ? <PauseIcon /> : <PlayIcon />}
            </button>
          </div>
          <p className="text-xs text-gray-500 text-center mt-2">
            Compare your waveform shape with the reference above.
          </p>
        </div>
      )}
    </div>
  );
};

export default WaveformPlayer;
